class ProductQuantityCartModule {
  constructor(controller) {
    this.controller = controller;
    this.section = controller.section;
    this.form = controller.query('[data-product-form]');
    this.submitButton = controller.query('[data-submit-button]');
    this.paymentButtonWrapper = controller.query('.product-form__payment-button');
    this.variantSelect = controller.query('[data-variant-select]');
    this.quantityInput = controller.query('[data-quantity-input]');
    this.minusButton = controller.query('[data-quantity-minus]');
    this.plusButton = controller.query('[data-quantity-plus]');
    this.dynamicCheckoutSyncToken = 0;
    this.inventorySyncToken = 0;

    if (!this.form) return;

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleCartRefresh = this.handleCartRefresh.bind(this);

    this.initQuantitySelector();
    this.initAjaxAddToCart();
    this.initDynamicCheckoutGuard();
    this.bindInventoryEvents();
    this.syncInventoryLimit();
  }

  bindInventoryEvents() {
    this.section.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('cart:refresh', this.handleCartRefresh);
  }

  handleVariantChange() {
    this.syncInventoryLimit();
  }

  handleCartRefresh() {
    this.syncInventoryLimit();
  }

  getSelectedVariantOption() {
    return this.variantSelect?.selectedOptions?.[0] || null;
  }

  getDefaultMax() {
    const fallback = Number.parseInt(this.quantityInput?.dataset.defaultMax || '99', 10);
    return Number.isNaN(fallback) || fallback < 1 ? 99 : fallback;
  }

  getQuantityValue() {
    const rawValue = Number.parseInt(this.quantityInput?.value || '1', 10);
    const min = Number.parseInt(this.quantityInput?.min || '1', 10) || 1;
    const max = Number.parseInt(this.quantityInput?.max || `${this.getDefaultMax()}`, 10) || this.getDefaultMax();
    const safeValue = Number.isNaN(rawValue) ? min : Math.min(Math.max(rawValue, min), max);

    if (this.quantityInput) {
      this.quantityInput.value = `${safeValue}`;
    }

    return safeValue;
  }

  updateQuantityButtonState() {
    if (!this.quantityInput) return;

    const value = this.getQuantityValue();
    const min = Number.parseInt(this.quantityInput.min, 10) || 1;
    const max = Number.parseInt(this.quantityInput.max, 10) || this.getDefaultMax();

    if (this.minusButton) {
      this.minusButton.disabled = value <= min;
    }

    if (this.plusButton) {
      this.plusButton.disabled = value >= max;
    }
  }

  applyQuantityBounds(nextMax) {
    if (!this.quantityInput) return;

    const min = Number.parseInt(this.quantityInput.min, 10) || 1;
    const safeMax = Math.max(Number.parseInt(nextMax, 10) || this.getDefaultMax(), min);
    const currentValue = Number.parseInt(this.quantityInput.value, 10);
    const clampedValue = Number.isNaN(currentValue) ? min : Math.min(Math.max(currentValue, min), safeMax);

    this.quantityInput.max = `${safeMax}`;
    this.quantityInput.value = `${clampedValue}`;
    this.updateQuantityButtonState();
  }

  async syncInventoryLimit() {
    if (!this.quantityInput || !window.CartApi) {
      this.updateQuantityButtonState();
      return;
    }

    const variantOption = this.getSelectedVariantOption();
    const defaultMax = this.getDefaultMax();
    const syncToken = ++this.inventorySyncToken;

    if (!variantOption || !CartApi.isVariantStockLimited(variantOption)) {
      this.quantityInput.dataset.stockRemaining = '';
      this.applyQuantityBounds(defaultMax);
      this.syncDynamicCheckoutState();
      return;
    }

    let cart = CartApi.getCartCache();

    if (!Array.isArray(cart?.items)) {
      try {
        cart = await CartApi.getCart();
      } catch (_) {
        if (syncToken !== this.inventorySyncToken) return;
        this.quantityInput.dataset.stockRemaining = '';
        this.applyQuantityBounds(defaultMax);
        this.syncDynamicCheckoutState();
        return;
      }
    }

    if (syncToken !== this.inventorySyncToken) return;

    const inventoryLimit = CartApi.getVariantInventoryLimit(variantOption);
    const inCartQuantity = CartApi.getVariantCartQuantity(cart, variantOption.value);
    const remainingQuantity = Math.max((inventoryLimit ?? defaultMax) - inCartQuantity, 0);

    this.quantityInput.dataset.stockRemaining = `${remainingQuantity}`;
    this.applyQuantityBounds(remainingQuantity > 0 ? remainingQuantity : 1);
    this.syncDynamicCheckoutState();
  }

  initQuantitySelector() {
    if (!this.quantityInput) return;

    this.minusButton?.addEventListener('click', () => {
      const currentValue = this.getQuantityValue();
      const min = Number.parseInt(this.quantityInput.min, 10) || 1;

      if (currentValue > min) {
        this.quantityInput.value = `${currentValue - 1}`;
      }

      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    this.plusButton?.addEventListener('click', () => {
      const currentValue = this.getQuantityValue();
      const max = Number.parseInt(this.quantityInput.max, 10) || this.getDefaultMax();

      if (currentValue < max) {
        this.quantityInput.value = `${currentValue + 1}`;
      }

      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    this.quantityInput.addEventListener('change', () => {
      this.getQuantityValue();
      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    this.quantityInput.addEventListener('input', () => {
      this.getQuantityValue();
      this.updateQuantityButtonState();
      this.syncDynamicCheckoutState();
    });

    this.updateQuantityButtonState();
  }

  initAjaxAddToCart() {
    this.form.addEventListener('submit', async (event) => {
      if (!window.CartApi?.addToCart || !window.CartApi?.refreshCart) return;

      event.preventDefault();

      if (!this.submitButton) return;

      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.classList.add('loading');

      try {
        const formData = new FormData(this.form);
        const selectedVariantId = this.variantSelect?.value || this.getSelectedVariantOption()?.value || '';
        if (selectedVariantId) {
          formData.set('id', selectedVariantId);
        }

        const cartResponse = await CartApi.addToCart(formData, {
          variantData: this.getSelectedVariantOption(),
        });

        await CartApi.refreshCart(cartResponse);
        (
          document.getElementById('simple-header-cart-trigger') ||
          document.getElementById('custom-header-cart-trigger')
        )?.click();
      } catch (error) {
        CartApi.showToast(error.message, true);
      } finally {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('loading');
      }
    });
  }

  initDynamicCheckoutGuard() {
    if (!this.paymentButtonWrapper || !window.CartApi?.isHighRiskManagementEnabled?.()) return;

    this.syncDynamicCheckoutState();

    document.addEventListener('cart:refresh', () => {
      this.syncDynamicCheckoutState();
    });

    this.paymentButtonWrapper.addEventListener(
      'click',
      (event) => {
        if (this.paymentButtonWrapper.dataset.highRiskBlocked !== 'true') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        CartApi.showToast(CartApi.getHighRiskLimitMessage(), true);
      },
      true
    );
  }

  async syncDynamicCheckoutState() {
    if (!this.paymentButtonWrapper || !window.CartApi?.isHighRiskManagementEnabled?.()) return;

    const syncToken = ++this.dynamicCheckoutSyncToken;
    const quantity = this.getQuantityValue();
    let cart = CartApi.getCartCache();

    if (!Array.isArray(cart?.items)) {
      try {
        cart = await CartApi.getCart();
      } catch (_) {
        return;
      }
    }

    if (syncToken !== this.dynamicCheckoutSyncToken) return;

    const projectedTotal = CartApi.getItemCount(cart) + quantity;
    const isBlocked = CartApi.cartHitsHighRiskLimit(projectedTotal);
    this.paymentButtonWrapper.dataset.highRiskBlocked = isBlocked ? 'true' : 'false';
    this.paymentButtonWrapper.classList.toggle('product-form__payment-button--blocked', isBlocked);
    this.paymentButtonWrapper.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
  }
}

window.HerbiProductSection.registerModule('quantity-cart', (controller) => {
  return new ProductQuantityCartModule(controller);
});
